import React, { useState, useRef } from 'react';
import { Camera, Scan, Plus, X, Check } from 'lucide-react';

interface ScannedFood {
  id: string;
  name: string;
  brand?: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  servingSize: string;
  barcode?: string;
  imageUrl?: string;
}

interface FoodCardProps {
  food: ScannedFood;
  onRemove: (id: string) => void;
  onAdd: (food: ScannedFood) => void;
}

const FoodCard: React.FC<FoodCardProps> = ({ food, onRemove, onAdd }) => {
  return (
    <div 
      className="bg-white rounded-xl p-4 shadow-md border"
      style={{ 
        boxShadow: 'var(--shadow-md)',
        border: '1px solid rgba(0,0,0,0.08)'
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="font-semibold text-base" style={{ color: 'var(--color-text)' }}>
            {food.name}
          </h4>
          {food.brand && (
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {food.brand}
            </p>
          )}
        </div>
        <button
          onClick={() => onRemove(food.id)}
          className="p-1 rounded-full hover:bg-gray-100"
        >
          <X className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(58, 175, 169, 0.1)' }}>
          <span className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>
            {food.calories}
          </span>
          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>calories</p>
        </div>
        <div className="text-center p-2 rounded-lg" style={{ background: 'rgba(156, 106, 222, 0.1)' }}>
          <span className="text-lg font-bold" style={{ color: 'var(--color-secondary)' }}>
            {food.protein}g
          </span>
          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>protein</p>
        </div>
      </div>
      
      <div className="flex items-center justify-between">
        <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
          {food.servingSize}
        </span>
        <button
          onClick={() => onAdd(food)}
          className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
          style={{
            background: 'var(--gradient-primary)',
            color: 'var(--color-white)',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <Plus className="h-4 w-4 inline mr-1" />
          Add
        </button>
      </div>
    </div>
  );
};

const FoodScanPanel: React.FC = () => {
  const [scannedFoods, setScannedFoods] = useState<ScannedFood[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanMode, setScanMode] = useState<'camera' | 'barcode'>('camera');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const mockFoods: ScannedFood[] = [
    {
      id: '1',
      name: 'Greek Yogurt',
      brand: 'Chobani',
      calories: 130,
      protein: 20,
      carbs: 6,
      fat: 0,
      servingSize: '1 cup (170g)',
      barcode: '894700010045'
    },
    {
      id: '2',
      name: 'Almonds',
      brand: 'Blue Diamond',
      calories: 160,
      protein: 6,
      carbs: 6,
      fat: 14,
      servingSize: '1 oz (28g)',
      barcode: '041570054321'
    },
    {
      id: '3',
      name: 'Banana',
      calories: 105,
      protein: 1,
      carbs: 27,
      fat: 0,
      servingSize: '1 medium (118g)'
    }
  ];

  const handleScanPhoto = () => {
    setIsScanning(true);
    setTimeout(() => {
      const randomFood = mockFoods[Math.floor(Math.random() * mockFoods.length)];
      setScannedFoods(prev => [...prev, { ...randomFood, id: Date.now().toString() }]);
      setIsScanning(false);
    }, 2000);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsScanning(true);
      setTimeout(() => {
        const randomFood = mockFoods[Math.floor(Math.random() * mockFoods.length)];
        setScannedFoods(prev => [...prev, { ...randomFood, id: Date.now().toString() }]);
        setIsScanning(false);
      }, 2000);
    }
  };

  const handleRemoveFood = (id: string) => {
    setScannedFoods(prev => prev.filter(food => food.id !== id));
  };

  const handleAddFood = (food: ScannedFood) => {
    console.log('Adding food to diary:', food);
  };

  const totalCalories = scannedFoods.reduce((sum, food) => sum + food.calories, 0);
  const totalProtein = scannedFoods.reduce((sum, food) => sum + food.protein, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>
          Food Scanner
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setScanMode('camera')}
            className={`p-3 rounded-lg transition-all duration-200 ${
              scanMode === 'camera' ? 'shadow-lg' : 'shadow-sm'
            }`}
            style={{
              background: scanMode === 'camera' ? 'var(--gradient-primary)' : 'var(--gradient-card-bg)',
              color: scanMode === 'camera' ? 'var(--color-white)' : 'var(--color-text-muted)'
            }}
          >
            <Camera className="h-5 w-5" />
          </button>
          <button
            onClick={() => setScanMode('barcode')}
            className={`p-3 rounded-lg transition-all duration-200 ${
              scanMode === 'barcode' ? 'shadow-lg' : 'shadow-sm'
            }`}
            style={{
              background: scanMode === 'barcode' ? 'var(--gradient-primary)' : 'var(--gradient-card-bg)',
              color: scanMode === 'barcode' ? 'var(--color-white)' : 'var(--color-text-muted)'
            }}
          >
            <Scan className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6" style={{ boxShadow: 'var(--shadow-lg)' }}>
        <div className="text-center">
          {isScanning ? (
            <div className="py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--color-primary)' }}></div>
              <p className="text-lg font-medium" style={{ color: 'var(--color-text)' }}>
                Analyzing {scanMode === 'camera' ? 'photo' : 'barcode'}...
              </p>
              <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>
                Identifying nutritional information
              </p>
            </div>
          ) : (
            <div className="py-12">
              <div 
                className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ background: 'var(--gradient-primary)' }}
              >
                {scanMode === 'camera' ? (
                  <Camera className="h-12 w-12" style={{ color: 'var(--color-white)' }} />
                ) : (
                  <Scan className="h-12 w-12" style={{ color: 'var(--color-white)' }} />
                )}
              </div>
              
              <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text)' }}>
                {scanMode === 'camera' ? 'Scan Your Meal' : 'Scan Barcode'}
              </h3>
              <p className="text-base mb-6" style={{ color: 'var(--color-text-muted)' }}>
                {scanMode === 'camera' 
                  ? 'Take a photo of your food to automatically identify nutritional information'
                  : 'Scan the barcode on packaged foods for instant nutrition data'
                }
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={handleScanPhoto}
                  className="w-full py-4 px-6 rounded-lg font-semibold transition-all duration-200"
                  style={{
                    background: 'var(--gradient-primary)',
                    color: 'var(--color-white)',
                    boxShadow: 'var(--shadow-md)'
                  }}
                >
                  {scanMode === 'camera' ? 'Take Photo' : 'Scan Barcode'}
                </button>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-3 px-6 rounded-lg font-medium transition-all duration-200"
                  style={{
                    background: 'var(--gradient-card-bg)',
                    color: 'var(--color-text)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: 'var(--shadow-sm)'
                  }}
                >
                  Upload from Gallery
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {scannedFoods.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>
              Scanned Items ({scannedFoods.length})
            </h3>
            <div className="flex items-center space-x-4">
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                Total: {totalCalories} cal, {totalProtein}g protein
              </span>
            </div>
          </div>
          
          <div className="grid gap-4">
            {scannedFoods.map((food) => (
              <FoodCard
                key={food.id}
                food={food}
                onRemove={handleRemoveFood}
                onAdd={handleAddFood}
              />
            ))}
          </div>
          
          <button
            className="w-full py-4 px-6 rounded-lg font-semibold transition-all duration-200"
            style={{
              background: 'var(--gradient-secondary)',
              color: 'var(--color-white)',
              boxShadow: 'var(--shadow-md)'
            }}
          >
            <Check className="h-5 w-5 inline mr-2" />
            Add All to Food Diary
          </button>
        </div>
      )}
    </div>
  );
};

export default FoodScanPanel;
